import React from "react";
import { createBrowserHistory as createHistory } from "history";
import simpleRestProvider from "ra-data-simple-rest";
import {
  Admin,
  fetchUtils,
  Resource,
  CustomRoutes,
  ResourceContextProvider,
} from "react-admin";
import { Route, Navigate } from "react-router";
import MyLayout from "./components/AdminLayout";
import Dashboard from "./pages/Dashboard";
import LoginPage from "./pages/Login";
import { ProfileEdit } from "./pages/ProfileEdit";
import Register from "./pages/Register";
import authProvider from "./providers/authProvider";
import { basePath } from "./providers/env";
import { Context } from "./pages/Context";
import Settings from "@mui/icons-material/Settings";
import DashboardCustomize from "@mui/icons-material/DashboardCustomize";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  DynamicContextCreate,
  DynamicContextEdit,
  DynamicContextList,
} from "./pages/DynamicContext";
import { LiveChatSettingForm } from "./pages/LiveChatSetting";
import {
  TransferCaseCreate,
  TransferCaseEdit,
  TransferCaseList,
} from "./pages/TransferCase";
import { ApiList } from "./pages/ApiManagement";
import { TestApiForm } from "./pages/ApiManagement/TestApi";
import { QuestionEdit, QuestionList } from "./pages/Questions";
import {
  ApiVariablesCreate,
  ApiVariablesEdit,
  ApiVariablesList,
} from "./pages/ApiVariables";
import { ZaloSettingForm } from "./pages/LiveChatSetting/Zalo";
import { WhatsappForm } from "./pages/LiveChatSetting/Whatsapp";
import { TawkToForm } from "./pages/LiveChatSetting/TawkTo";
import AgentList from "./pages/Agents";
import { UserCreate, UserList } from "./pages/Users";
import { Box, Button, Typography } from "@mui/material";
import NotFoundIcon from "@mui/icons-material/ErrorOutline";
import { useParams } from "react-router-dom";
import BillingPage from "./pages/Invoice";
import { ChatsPage } from "./pages/Conversation";
import { LineForm } from "./pages/LiveChatSetting/Line";
import RolesAndPermissionsPage from "./pages/RolesAndPermissions";
import SuperAdminLayout from "./components/SuperAdminLayout";
import AccessDenied from "./pages/AccessDenied";
import { ProtectedRoute } from "./components/ProtectedRoute";

const httpClient = (url: string, options: any = {}) => {
  options.user = {
    authenticated: true,
    token: `Bearer ${localStorage.getItem("token")}`,
  };
  if (url.includes("/users/") && options.method === "PUT") {
    options.method = "PATCH";
  }
  return fetchUtils.fetchJson(url, options);
};

const dataProvider = simpleRestProvider(`${basePath}/api/v1`, httpClient);

const getAgentIdForResource = (resource: string) => {
  try {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const idx = parts.indexOf(resource);
    if (idx >= 0 && parts.length > idx + 1) {
      return parts[idx + 1];
    }
    return null;
  } catch (e) {
    return null;
  }
};

const customDataProvider = {
  ...dataProvider,

  async getList(resource: any, params: any) {
    const buildQueryWithAgent = (baseParams: Record<string, string>) => {
      const queryParams = new URLSearchParams(baseParams);
      const agentId = getAgentIdForResource(resource);
      if (agentId) {
        queryParams.append("agent_id", agentId);
      }
      return queryParams;
    };

    if (resource === "unanswered-question") {
      const { page, perPage } = params.pagination;
      const { keyword } = params.filter || {};

      const queryParams = buildQueryWithAgent({
        page: String(page),
        page_size: String(perPage),
      });
      if (keyword) {
        queryParams.append("keyword", keyword);
      }
      const url = `${basePath}/api/v1/unanswered-question?${queryParams}`;
      const { json } = await httpClient(url);

      return {
        data: json.data,
        total: json.total,
      };
    }

    if (resource === "customer-data") {
      const { page, perPage } = params.pagination;
      const { keyword } = params.filter || {};
      const queryParams = buildQueryWithAgent({
        page: String(page),
        page_size: String(perPage),
      });
      if (keyword) {
        queryParams.append("keyword", keyword);
      }
      const url = `${basePath}/api/v1/customer-data?${queryParams}`;
      const { json } = await httpClient(url);

      return {
        data: json.data,
        total: json.total,
      };
    }

    if (resource === "environ-variables") {
      const { page, perPage } = params.pagination;
      const { keyword } = params.filter || {};
      const queryParams = buildQueryWithAgent({
        page: String(page),
        page_size: String(perPage),
      });
      if (keyword) {
        queryParams.append("keyword", keyword);
      }
      const url = `${basePath}/api/v1/environ-variables?${queryParams}`;
      const { json } = await httpClient(url);

      return {
        data: json.data,
        total: json.total,
      };
    }

    if (resource === "vendor-api-master") {
      const { page, perPage } = params.pagination;
      const { keyword } = params.filter || {};
      const queryParams = buildQueryWithAgent({
        page: String(page),
        page_size: String(perPage),
      });
      if (keyword) {
        queryParams.append("keyword", keyword);
      }
      const url = `${basePath}/api/v1/vendor-api-master?${queryParams}`;
      const { json } = await httpClient(url);

      return {
        data: json.data,
        total: json.total,
      };
    }

    if (resource === "chat-transfer-cases") {
      const { page, perPage } = params.pagination;
      const { keyword } = params.filter || {};
      const queryParams = buildQueryWithAgent({
        page: String(page),
        page_size: String(perPage),
      });
      if (keyword) {
        queryParams.append("keyword", keyword);
      }
      const url = `${basePath}/api/v1/chat-transfer-cases?${queryParams}`;
      const { json } = await httpClient(url);

      return {
        data: json.data,
        total: json.total,
      };
    }

    return dataProvider.getList(resource, params);
  },
};

// Simple NotFound page
const NotFoundPage: React.FC<{ reason?: string }> = ({ reason }) => {
  return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <NotFoundIcon sx={{ fontSize: 80, opacity: 0.6 }} />
      <Typography variant="h4" gutterBottom>
        404 — Not Found
      </Typography>
      {reason ? (
        <Typography variant="body1">{reason}</Typography>
      ) : (
        <Typography variant="body2">
          The page you are looking for does not exist.
        </Typography>
      )}
      <Button sx={{ mt: 3 }} variant="contained" href="/agents">
        Back to Agents
      </Button>
    </Box>
  );
};

// Component to catch unknown resource routes like /:resource/:agentId/*
const ResourceNotFoundCatcher: React.FC = () => {
  const params = useParams() as { resource?: string; agentId?: string };
  const { resource, agentId } = params;

  const reason = resource
    ? `No page found for resource "${resource}" under agent "${agentId}".`
    : "No matching route found.";

  return <NotFoundPage reason={reason} />;
};

// Build children as a function, then cast to React.ReactNode to satisfy react-admin's TypeScript signature
const renderAdminChildren = (permissions: any) => [
  // Super Admin routes - MUST BE FIRST (noLayout to bypass react-admin Layout)
  <CustomRoutes key="super-admin-routes" noLayout>
  <Route path="/super-admin/*" element={<SuperAdminLayout />}>
    <Route path="dashboard" element={<RolesAndPermissionsPage />} />
    <Route path="organizations" element={<div>Organizations Page</div>} />
    <Route path="analytics" element={<div>Analytics Page</div>} />
    <Route path="roles-and-permissions" element={<RolesAndPermissionsPage />} />
    <Route path="settings" element={<div>Settings Page</div>} />
    <Route index element={<Navigate to="dashboard" replace />} />
  </Route>
</CustomRoutes>,

  <CustomRoutes key="root-redirect">
    <Route path="/" element={<Navigate to="/agents" replace />} />
  </CustomRoutes>,

  <CustomRoutes key="no-agentid-routes">
    <Route
      path="/context"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/customer-data"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/unanswered-question"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/environ-variables"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/vendor-api-master"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/apiSettings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/config-settings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/zalo-config-settings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/whatsapp-config-settings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/tawkto-config-settings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/line-config-settings"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/chat-transfer-cases"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
    <Route
      path="/dashboard"
      element={<NotFoundPage reason="Missing agentId in URL" />}
    />
  </CustomRoutes>,

  <CustomRoutes key="my-profile">
    <Route path="/my-profile" element={<ProfileEdit />} />
  </CustomRoutes>,

  <CustomRoutes key="access-denied">
    <Route path="/access-denied" element={<AccessDenied />} />
  </CustomRoutes>,

  permissions.is_superuser === true ? (
    <CustomRoutes key="users">
      <Route
        path="/users"
        element={
          <ResourceContextProvider value="users">
            <UserList />
          </ResourceContextProvider>
        }
      />
      <Route
        path="/users/create"
        element={
          <ResourceContextProvider value="users">
            <UserCreate />
          </ResourceContextProvider>
        }
      />
    </CustomRoutes>
  ) : null,

  <CustomRoutes key="roles-and-permissions">
    <Route
      path="/roles-and-permissions"
      element={
        <ProtectedRoute
          anyPermissions={[
            { resource: 'user', action: 'view' },
            { resource: 'role', action: 'view' },
          ]}
        >
          <RolesAndPermissionsPage />
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="agents-route">
    <Route
      path="/agents"
      element={
        <ResourceContextProvider value="agents">
          <AgentList />
        </ResourceContextProvider>
      }
    />
  </CustomRoutes>,

  <CustomRoutes noLayout key="register">
    <Route path="/register" element={<Register />} />
  </CustomRoutes>,

  <CustomRoutes key="dashboard-route">
    <Route
      path="/dashboard/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'agent', action: 'view' }}>
          <Dashboard />
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <Resource
    key="context-resource"
    name="context"
    options={{ label: "Context" }}
    icon={DashboardCustomize}
  />,
  <Resource
    key="customer-data-resource"
    name="customer-data"
    options={{ label: "Dynamic Context" }}
    icon={DashboardCustomize}
    list={DynamicContextList}
    edit={DynamicContextEdit}
    create={DynamicContextCreate}
  />,
  <Resource
    key="unanswered-question-resource"
    name="unanswered-question"
    options={{ label: "Questions" }}
    icon={DashboardCustomize}
    list={QuestionList}
    edit={QuestionEdit}
  />,
  <Resource
    key="environ-variables-resource"
    name="environ-variables"
    options={{ label: "Apis Variables" }}
    icon={DashboardCustomize}
    list={ApiVariablesList}
    create={ApiVariablesCreate}
    edit={ApiVariablesEdit}
  />,
  <Resource
    key="vendor-api-master-resource"
    name="vendor-api-master"
    options={{ label: "Apis" }}
    icon={DashboardCustomize}
    list={ApiList}
    edit={TestApiForm}
  />,
  <Resource
    key="apiSettings-resource"
    name="apiSettings"
    options={{ label: "Test Apis" }}
    icon={DashboardCustomize}
  />,
  <Resource
    key="config-settings-resource"
    name="config-settings"
    options={{ label: "Live Chat Inc Setting" }}
    icon={Settings}
  />,
  <Resource
    key="zalo-config-settings-resource"
    name="zalo-config-settings"
    options={{ label: "Zalo Inc Setting" }}
    icon={Settings}
  />,
  <Resource
    key="whatsapp-config-settings-resource"
    name="whatsapp-config-settings"
    options={{ label: "Whatsapp Inc Setting" }}
    icon={Settings}
  />,
  <Resource
    key="tawkto-config-settings-resource"
    name="tawkto-config-settings"
    options={{ label: "TawkTo Inc Setting" }}
    icon={Settings}
  />,
  <Resource
    key="line-config-settings-resource"
    name="line-config-settings"
    options={{ label: "Line Inc Setting" }}
    icon={Settings}
  />,
  <Resource
    key="chat-transfer-cases-resource"
    name="chat-transfer-cases"
    options={{ label: "Transfer Case" }}
    icon={Settings}
    list={TransferCaseList}
    create={TransferCaseCreate}
    edit={TransferCaseEdit}
  />,

  <Resource
    key="invoice-resource"
    options={{ label: "Invoice" }}
    name="invoice"
    list={BillingPage}
    icon={ReceiptIcon}
  />,

  <CustomRoutes key="context-agent">
    <Route
      path="/context/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'context', action: 'view' }}>
          <ResourceContextProvider value="context">
            <Context />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="customer-data-routes">
    <Route
      path="/customer-data/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'customer_data', action: 'view' }}>
          <ResourceContextProvider value="customer-data">
            <DynamicContextList />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/customer-data/:agentId/create"
      element={
        <ProtectedRoute permission={{ resource: 'customer_data', action: 'create' }}>
          <ResourceContextProvider value="customer-data">
            <DynamicContextCreate />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/customer-data/:agentId/:id"
      element={
        <ProtectedRoute permission={{ resource: 'customer_data', action: 'edit' }}>
          <ResourceContextProvider value="customer-data">
            <DynamicContextEdit />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="unanswered-question-routes">
    <Route
      path="/unanswered-question/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'unanswered_question', action: 'view' }}>
          <ResourceContextProvider value="unanswered-question">
            <QuestionList />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/unanswered-question/:agentId/:id"
      element={
        <ProtectedRoute permission={{ resource: 'unanswered_question', action: 'edit' }}>
          <ResourceContextProvider value="unanswered-question">
            <QuestionEdit />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="environ-variables-routes">
    <Route
      path="/environ-variables/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'environ_variables', action: 'view' }}>
          <ResourceContextProvider value="environ-variables">
            <ApiVariablesList />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/environ-variables/:agentId/create"
      element={
        <ProtectedRoute permission={{ resource: 'environ_variables', action: 'create' }}>
          <ResourceContextProvider value="environ-variables">
            <ApiVariablesCreate />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/environ-variables/:agentId/:id"
      element={
        <ProtectedRoute permission={{ resource: 'environ_variables', action: 'edit' }}>
          <ResourceContextProvider value="environ-variables">
            <ApiVariablesEdit />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="vendor-api-master-routes">
    <Route
      path="/vendor-api-master/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'vendor_api', action: 'view' }}>
          <ResourceContextProvider value="vendor-api-master">
            <ApiList />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/vendor-api-master/:agentId/:id"
      element={
        <ProtectedRoute permission={{ resource: 'vendor_api', action: 'edit' }}>
          <ResourceContextProvider value="vendor-api-master">
            <TestApiForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/vendor-api-master/:agentId/create"
      element={
        <ProtectedRoute permission={{ resource: 'vendor_api', action: 'create' }}>
          <ResourceContextProvider value="vendor-api-master">
            <TestApiForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/vendor-api-master/:agentId/duplicate-api/:id"
      element={
        <ProtectedRoute permission={{ resource: 'vendor_api', action: 'create' }}>
          <ResourceContextProvider value="vendor-api-master">
            <TestApiForm duplicate={true} />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="apiSettings-routes">
    <Route
      path="/apiSettings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'vendor_api', action: 'view' }}>
          <ResourceContextProvider value="apiSettings">
            <TestApiForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="livechat-settings">
    <Route
      path="/config-settings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'livechat_settings', action: 'view' }}>
          <ResourceContextProvider value="config-settings">
            <LiveChatSettingForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/zalo-config-settings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'zalo_settings', action: 'view' }}>
          <ResourceContextProvider value="zalo-config-settings">
            <ZaloSettingForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/whatsapp-config-settings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'whatsapp_settings', action: 'view' }}>
          <ResourceContextProvider value="whatsapp-config-settings">
            <WhatsappForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/tawkto-config-settings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'tawkto_settings', action: 'view' }}>
          <ResourceContextProvider value="tawkto-config-settings">
            <TawkToForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/line-config-settings/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'line_settings', action: 'view' }}>
          <ResourceContextProvider value="line-config-settings">
            <LineForm />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="chat-transfer-cases">
    <Route
      path="/chat-transfer-cases/:agentId"
      element={
        <ProtectedRoute permission={{ resource: 'transfer_case', action: 'view' }}>
          <ResourceContextProvider value="chat-transfer-cases">
            <TransferCaseList />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/chat-transfer-cases/:agentId/create"
      element={
        <ProtectedRoute permission={{ resource: 'transfer_case', action: 'create' }}>
          <ResourceContextProvider value="chat-transfer-cases">
            <TransferCaseCreate />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="/chat-transfer-cases/:agentId/:id"
      element={
        <ProtectedRoute permission={{ resource: 'transfer_case', action: 'edit' }}>
          <ResourceContextProvider value="chat-transfer-cases">
            <TransferCaseEdit />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="chats-route">
    <Route
      path="/chats"
      element={
        <ProtectedRoute permission={{ resource: 'chat', action: 'view' }}>
          <ResourceContextProvider value="chats">
            <ChatsPage />
          </ResourceContextProvider>
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,

  <CustomRoutes key="resource-not-found">
    <Route path="/:resource/:agentId/*" element={<ResourceNotFoundCatcher />} />
  </CustomRoutes>,
];

const App = () => {
  return (
    <Admin
      disableTelemetry
      dataProvider={customDataProvider}
      authProvider={authProvider}
      loginPage={LoginPage}
      history={createHistory()}
      layout={MyLayout}
    >
      {(permissions) => renderAdminChildren(permissions)}
    </Admin>
  );
};

export default App;